import AdminHeading from '@/components/admin/heading/AdminHeading';
import {
  useActivateCompanyMutation,
  useGetCompaniesIdQuery
} from '@/services/company.service';
import { formatReadableDate } from '@/utils/helper';
import {
  Box,
  Paper,
  Grid,
  GridCol,
  TextInput,
  Loader,
  Flex,
  Title,
  Center,
  Stack,
  ThemeIcon,
  Text,
  Switch,
  Button
} from '@inexture/core';
import { useState } from 'react';
import { FaBuilding } from 'react-icons/fa';
import { useLocation, useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';

const CompanyDetailed = () => {
  const { id: companyId } = useParams();
  const location = useLocation();
  const status = location.pathname.split('/')[3];
  const navigate = useNavigate();
  const [activateCompany, { isLoading: activationLoading }] =
    useActivateCompanyMutation();

  const { data: company, isLoading } = useGetCompaniesIdQuery({
    id: companyId ?? '',
    status: (status as 'active' | 'pending') ?? 'pending'
  });

  const [isActivated, setIsActivated] = useState(false);

  const handleActivate = async (activation_key: string) => {
    try {
      const res = await activateCompany({ activation_key }).unwrap();
      toast.success(res.message);
      toast.success('Redirected to Active Company List');
      setIsActivated(false);
      navigate('/admin/company/activated');
    } catch (error: any) {
      console.error('Activation error:', error);
      setIsActivated(false);
      toast.error(error?.data?.errors || "Het activeren van het bedrijf is mislukt.");
    }
  };

  if (isLoading || activationLoading) {
    return (
      <Box p="lg">
        <Paper radius="md" withBorder h={800}>
          <Center h="100%">
            <Loader size="lg" />
          </Center>
        </Paper>
      </Box>
    );
  }

  if (!company) {
    return (
      <Box p="lg">
        <Paper radius="md" withBorder h={800}>
          <Center h="100%">
            <Title order={4} c="gray">
              No company details found
            </Title>
          </Center>
        </Paper>
      </Box>
    );
  }

  return (
    <>
      <AdminHeading
        title={`${company?.first_name} ${company?.last_name} - ${company?.company_name} Details`}
      />
      <Box p="lg">
        <Paper radius="md" p="xl" withBorder h={800}>
          {/* Header Section */}
          <Paper h={300} p="lg" bg="#3f596bb0" pos="relative">
            <Flex
              pos="absolute"
              top="50%"
              left="50%"
              style={{ transform: 'translate(-50%, -50%)' }}
              align="center"
              justify="center"
              gap="md"
            >
              <ThemeIcon w="auto" h="auto" p="md" bg="#3f596bb0" radius="lg">
                <FaBuilding size={50} color="white" />
              </ThemeIcon>
              <Box>
                <Text fz="h1" fw={800} c="white">
                  {company.company_name}
                </Text>
                <Text c="white">{company.email}</Text>
              </Box>
            </Flex>
          </Paper>

          {/* Company Details */}
          <Grid gutter="xl" my={40}>
            <GridCol span={{ base: 12, md: 6 }}>
              <Stack gap="md">
                <TextInput
                  label="Company Name"
                  value={company.company_name || ''}
                  disabled
                />
                <TextInput label="Email" value={company.email || ''} disabled />
                <TextInput
                  label="Contact"
                  value={company.contact || company.mobile || ''}
                  disabled
                />
                <TextInput
                  label="Registration Number"
                  value={company.company_registration || ''}
                  disabled
                />
              </Stack>
            </GridCol>

            <GridCol span={{ base: 12, md: 6 }}>
              <Stack gap="md">
                <TextInput
                  label="Registration Comments"
                  value={company.registration_comments || ''}
                  disabled
                />
                <TextInput
                  label="Created At"
                  value={formatReadableDate(company.created_at)}
                  disabled
                />
                <TextInput
                  label="Tenant Schema Created"
                  value={company.tenant_schema_created ? 'Yes' : 'No'}
                  disabled
                />
                <TextInput
                  label="Profile Confirmed"
                  value={company.profile_confirmed ? 'Yes' : 'No'}
                  disabled
                />

                {/* Activation Switch */}
                <Flex justify="end" align="center" mt="lg" gap="md">
                  {status === 'pending' && (
                    <Switch
                      label="Activate Company"
                      size="lg"
                      withThumbIndicator={false}
                      checked={isActivated}
                      onChange={e => {
                        const checked = e.currentTarget.checked;
                        if (checked) handleActivate(company.activation_key);
                        setIsActivated(checked);
                      }}
                    />
                  )}
                  <Button onClick={() => navigate(-1)} size="md">
                    Back
                  </Button>
                </Flex>
              </Stack>
            </GridCol>
          </Grid>
        </Paper>
      </Box>
    </>
  );
};

export default CompanyDetailed;
